/**
 * scoring-i18n.ts — libellé localisé d'un critère de scoring / objectif.
 *
 * Source de vérité UNIQUE pour afficher le libellé d'un `ScoringCriterion`
 * (ou tout objet `{ label, labelFr, labelEn }`) selon la langue active.
 *
 * ⚠️ NE JAMAIS réécrire en ligne `lang === "fr" && c.labelFr ? c.labelFr : c.label`
 * (pattern fautif : il IGNORE `labelEn` → affichage FR en mode EN). Le test
 * `tests/i18n-no-raw-label.test.ts` échoue si ce pattern réapparaît dans le code.
 *
 * Règle : labelSpoken (langue d'entraînement) d'abord, s'il a été injecté par l'API ;
 * sinon EN → labelEn || label || labelFr ; FR → labelFr || label || labelEn.
 */
export function pickScoringLabel(
  c: {
    label?: string | null;
    labelFr?: string | null;
    labelEn?: string | null;
    labelSpoken?: string | null;
  } | null | undefined,
  lang: string | undefined,
): string {
  if (!c) return "";
  // LANGUE D'ENTRAÎNEMENT d'abord. Ce champ n'est présent que si l'API l'a injecté —
  // c'est-à-dire si le lecteur a une langue d'entraînement ET que le référentiel y est
  // traduit. En le traitant ICI, les sept écrans qui appellent déjà ce helper en
  // bénéficient sans qu'une ligne ne change chez eux. C'est tout l'intérêt d'avoir un
  // point de résolution unique : on n'y touche qu'une fois.
  const parle = (c.labelSpoken ?? "").trim();
  if (parle) return parle;

  const isEn = (lang || "fr").toLowerCase().startsWith("en");
  const picked = isEn
    ? (c.labelEn || c.label || c.labelFr)
    : (c.labelFr || c.label || c.labelEn);
  return picked || "";
}

/** Ce que portent un critère et ses traductions pour le comportement. */
type AvecComportement = {
  label?: string | null;
  labelFr?: string | null;
  labelEn?: string | null;
  labelSpoken?: string | null;
  behaviorFr?: string | null;
  behaviorEn?: string | null;
  /** Comportement dans la langue d'entraînement du LECTEUR — injecté par `injecterTraductions`. */
  behaviorSpoken?: string | null;
};

/**
 * Le COMPORTEMENT seul d'un critère (« Poser des questions ouvertes… »), dans la langue du
 * lecteur — `""` quand le critère n'en porte pas (décision 5.1 : seuls les 37 critères NOTÉS des
 * simulateurs en ont ; ni les objectifs oui/non, ni les critères des coachs).
 *
 * Même ordre que `pickScoringLabel` : la langue d'entraînement d'abord (`behaviorSpoken`, présent
 * seulement si l'API l'a injecté), puis EN → behaviorEn || behaviorFr, FR → behaviorFr || behaviorEn.
 * À utiliser quand l'écran montre critère ET comportement côte à côte (décision 5.2 bis) : il ne
 * faut surtout pas y retomber sur le libellé du critère, qui s'afficherait deux fois.
 */
export function pickBehaviorText(c: AvecComportement | null | undefined, lang: string | undefined): string {
  if (!c) return "";
  const parle = (c.behaviorSpoken ?? "").trim();
  if (parle) return parle;
  const isEn = (lang || "fr").toLowerCase().startsWith("en");
  const b = isEn ? (c.behaviorEn || c.behaviorFr) : (c.behaviorFr || c.behaviorEn);
  return (b ?? "").trim();
}

/**
 * Libellé de COMPORTEMENT d'un critère — « pose des questions ouvertes » plutôt que
 * « Qualité des questions ». Repli sur le libellé du critère (lui aussi dans la langue du
 * lecteur) quand aucun comportement n'est rédigé.
 */
export function pickBehaviorLabel(c: AvecComportement | null | undefined, lang: string | undefined): string {
  if (!c) return "";
  return pickBehaviorText(c, lang) || pickScoringLabel(c, lang);
}

/**
 * Le TEXTE D'AIDE d'un critère (sa définition, affichée au survol ET au toucher) dans la langue
 * du lecteur : `helpSpoken` (langue d'entraînement, injecté par `injecterTraductions`), puis
 * EN → helpEn || helpFr, FR → helpFr || helpEn. `""` sans texte d'aide.
 * Jamais envoyé à N8N : la description reste la source de la notation.
 */
export function pickCriterionHelp(
  c: { helpFr?: string | null; helpEn?: string | null; helpSpoken?: string | null } | null | undefined,
  lang: string | undefined,
): string {
  if (!c) return "";
  const parle = (c.helpSpoken ?? "").trim();
  if (parle) return parle;
  const isEn = (lang || "fr").toLowerCase().startsWith("en");
  const h = isEn ? (c.helpEn || c.helpFr) : (c.helpFr || c.helpEn);
  return (h ?? "").trim();
}

/**
 * Le VERDICT d'un objectif binaire — « Atteint » / « Non atteint », dans la langue du lecteur.
 *
 * Jumeau de `pickScoringLabel`, et pour la même raison : la règle vivait recopiée en ternaire
 * dans SIX composants (`lang === "fr" ? c.successLabel : c.successLabelEn`), ce qui l'a rendue
 * aveugle à la langue d'entraînement le jour où celle-ci est arrivée. Un apprenant russe lisait
 * le nom de son critère en russe et son verdict en français.
 *
 * Renvoie `""` quand le critère ne porte pas de verdict : l'appelant retombe alors sur sa clé
 * i18n d'interface (`t("history.passed")`), qui n'est pas une donnée de critère.
 */
export function pickVerdictLabel(
  c: {
    successLabel?: string | null;
    successLabelEn?: string | null;
    successLabelSpoken?: string | null;
    failLabel?: string | null;
    failLabelEn?: string | null;
    failLabelSpoken?: string | null;
  } | null | undefined,
  lang: string | undefined,
  atteint: boolean,
): string {
  if (!c) return "";
  // Langue d'entraînement d'abord — injectée par l'API quand le lecteur en a une.
  const parle = (atteint ? c.successLabelSpoken : c.failLabelSpoken) ?? "";
  if (parle.trim()) return parle.trim();

  const isEn = (lang || "fr").toLowerCase().startsWith("en");
  const fr = atteint ? c.successLabel : c.failLabel;
  const en = atteint ? c.successLabelEn : c.failLabelEn;
  const picked = isEn ? (en || fr) : (fr || en);
  return picked || "";
}
